/**
 * API client pour le module POS
 */
import { CreateSaleDto, CreateSaleResponse } from '../types/pos.types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

/**
 * Session expirée (14/10/2026). Ce client parle au serveur par `fetch` brut et
 * ignorait les refus 401, quand le client principal (services/api) renvoie à la
 * connexion. Sur un iPad à la session expirée, la fiche produit montrait alors
 * « 0 en stock » (lots refusés) pendant que la liste — publique — affichait le
 * vrai stock : un faux zéro sur lequel on pouvait « corriger le stock ».
 * Même règle que le client principal, sauf la CAISSE : on n'interrompt jamais
 * une vente en cours pour la renvoyer à l'écran de connexion.
 */
let sessionExpireeSignalee = false;
function surSessionExpiree() {
  const path = window.location.pathname;
  const exclue =
    path.startsWith('/pos') ||
    path.startsWith('/shop') ||
    path.startsWith('/sales/') ||
    path.startsWith('/login') ||
    path.startsWith('/signup');
  if (exclue || sessionExpireeSignalee) return;
  sessionExpireeSignalee = true;
  try { localStorage.removeItem('auth_token'); } catch { /* noop */ }
  window.location.href = '/login';
}

/** `fetch` de ce module : identique, plus la détection de session expirée. */
const fetch: typeof window.fetch = async (input, init) => {
  const reponse = await window.fetch(input, init);
  if (reponse.status === 401) surSessionExpiree();
  return reponse;
};

/**
 * Récupère le tenant ID depuis localStorage
 */
function getTenantId(): string | null {
  try {
    const tenantData = localStorage.getItem('tenant');
    if (tenantData) {
      const tenant = JSON.parse(tenantData);
      return tenant?.id || null;
    }
  } catch (e) {
    console.error('Failed to parse tenant data:', e);
  }
  return null;
}

/**
 * Récupère le token d'authentification depuis localStorage
 */
function getAuthToken(): string | null {
  // Priorité au customer_token (e-commerce), puis auth_token (staff)
  const customerToken = localStorage.getItem('customer_token');
  const staffToken = localStorage.getItem('auth_token');
  return customerToken || staffToken;
}

/**
 * Crée les headers avec X-Tenant-ID et Authorization
 */
function getHeaders(): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  // Ajouter le token JWT
  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Ajouter le tenant ID
  const tenantId = getTenantId();
  if (tenantId) {
    headers['X-Tenant-ID'] = tenantId;
  }

  return headers;
}

export const posApi = {
  /**
   * Ventes tombées dans une journée DÉJÀ clôturée : encaissées, valides, mais
   * dans aucun rapport Z — et plus moyen de les y faire entrer.
   */
  async getSalesHorsZ(from?: string): Promise<{
    count: number;
    total_cents: number;
    sales: Array<{
      id: string;
      sale_number: string;
      created_at: string;
      total_cents: number;
      payment_method: string;
      closing_report: string | null;
    }>;
  }> {
    const q = from ? `?from=${encodeURIComponent(from)}` : '';
    const response = await fetch(`${API_BASE_URL}/pos/sales/hors-z${q}`, { headers: getHeaders() });
    if (!response.ok) throw new Error('Impossible de lire les ventes hors Z');
    return response.json();
  },

  /**
   * Créer une vente
   */
  async createSale(saleDto: CreateSaleDto): Promise<CreateSaleResponse> {
    const response = await fetch(`${API_BASE_URL}/pos/sales`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(saleDto),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Erreur lors de la création de la vente');
    }

    return response.json();
  },

  /**
   * Récupérer les ventes du jour
   */
  async getTodaySales(tenantId: string) {
    const response = await fetch(`${API_BASE_URL}/pos/sales/today?tenant_id=${tenantId}`, {
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Erreur lors de la récupération des ventes');
    }

    return response.json();
  },

  /**
   * Récupérer une vente par ID
   */
  async getSale(saleId: string) {
    const response = await fetch(`${API_BASE_URL}/pos/sales/${saleId}`, {
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Erreur lors de la récupération de la vente');
    }

    return response.json();
  },

  /**
   * Annuler une vente
   */
  async cancelSale(saleId: string, reason: string) {
    const response = await fetch(`${API_BASE_URL}/pos/sales/${saleId}/cancel`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify({ reason }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Erreur lors de l\'annulation de la vente');
    }

    return response.json();
  },

  /**
   * Associer un client à une vente existante
   */
  async assignCustomer(saleId: string, customerId: string) {
    const response = await fetch(`${API_BASE_URL}/pos/sales/${saleId}/customer`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify({ customer_id: customerId }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Erreur lors de l\'association du client');
    }

    return response.json();
  },

  /**
   * Demander une facture pour une vente (sera créée dans le dashboard)
   */
  async requestInvoice(saleId: string) {
    const response = await fetch(`${API_BASE_URL}/pos/sales/${saleId}/request-invoice`, {
      method: 'PATCH',
      headers: getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Erreur lors de la demande de facture');
    }

    return response.json();
  },

  /**
   * Rembourser une vente (crée une vente négative)
   */
  async refundSale(saleId: string, reason: string) {
    const response = await fetch(`${API_BASE_URL}/pos/sales/${saleId}/refund`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ reason }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Erreur lors du remboursement de la vente');
    }

    return response.json();
  },

  /**
   * Récupérer les statistiques POS
   */
  async getStats(tenantId: string, startDate?: string, endDate?: string, vendorId?: string) {
    const params = new URLSearchParams({ tenant_id: tenantId });
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (vendorId) params.append('vendor_id', vendorId);

    const response = await fetch(`${API_BASE_URL}/pos/stats?${params.toString()}`, {
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Erreur lors de la récupération des statistiques');
    }

    return response.json();
  },

  /**
   * GESTION DES LOTS
   */

  /**
   * Récupérer tous les produits en réserve (backroom) avec leurs lots
   */
  async getBackroomLots() {
    const response = await fetch(`${API_BASE_URL}/lots/backroom`, {
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Erreur lors de la récupération des lots en réserve');
    }

    return response.json();
  },

  /**
   * Transférer un lot de la réserve vers le rayon.
   *
   * Mouvement de stock uniquement : on n'envoie PLUS `is_weighted`. L'unité de vente
   * (kg / pièce) est décidée à la réception et vit sur la fiche produit ; la mise en
   * rayon n'a aucune raison de la réécrire (cf. olives Kalamata, 05/08/2026).
   */
  async moveToDisplay(lotId: string, quantity: number, createdBy?: string) {
    const response = await fetch(`${API_BASE_URL}/lots/${lotId}/move-to-display`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ quantity, created_by: createdBy }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Erreur lors du transfert vers le rayon');
    }

    return response.json();
  },

  /**
   * Annoncer un produit en nouveauté. La fin de l'annonce est calculée par le
   * SERVEUR (prochaine ouverture de la boutique, plancher de cinq jours) : le
   * client ne décide pas d'une durée, il pose un geste.
   */
  async marquerNouveaute(productId: string, kind: 'NEW' | 'BACK' = 'NEW', vendorId?: string | null) {
    const response = await fetch(`${API_BASE_URL}/products/${productId}/novelty`, {
      method: 'PATCH',
      headers: getHeaders(),
      // Le vendeur voyage dans le corps : au POS, le jeton HTTP est celui du
      // personnel, l'identité du vendeur vit dans la session (même motif que
      // `recorded_by` sur les sorties de stock).
      body: JSON.stringify({ kind, vendor_id: vendorId || undefined }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || "Erreur lors de l'annonce de la nouveauté");
    }

    return response.json();
  },

  /**
   * Noter « rayon plein » pour un produit : ce qui TIENT sur l'étal, dans l'unité
   * de vente. Une valeur saisie prime sur la déduction nocturne et n'est plus
   * jamais écrasée par elle ; `null` l'efface et rend la main à l'observation.
   */
  async setShelfCapacity(productId: string, capacity: number | null) {
    const response = await fetch(
      `${API_BASE_URL}/pos/restock-alerts/product/${productId}/shelf-capacity`,
      {
        method: 'PATCH',
        headers: getHeaders(),
        body: JSON.stringify({ capacity }),
      },
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || "Erreur lors de l'enregistrement du rayon plein");
    }

    return response.json();
  },

  /**
   * Transférer un lot du rayon vers la réserve
   */
  async moveToBackroom(lotId: string, quantity: number, createdBy?: string) {
    const response = await fetch(`${API_BASE_URL}/lots/${lotId}/move-to-backroom`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ quantity, created_by: createdBy }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Erreur lors du transfert vers la réserve');
    }

    return response.json();
  },

  /**
   * Récupérer l'historique des mouvements d'un lot
   */
  async getLotMovements(lotId: string) {
    const response = await fetch(`${API_BASE_URL}/lots/${lotId}/movements`, {
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Erreur lors de la récupération de l\'historique du lot');
    }

    return response.json();
  },

  /**
   * Récupérer tous les lots d'un produit
   */
  /**
   * Le dernier lot RECU d'un produit, epuise compris.
   * `getProductLots` ne rend que les lots actifs : quand le dernier est solde,
   * la fiche perd la DLC, la date de reception et le fournisseur.
   */
  async getLastReceivedLot(productId: string) {
    const response = await fetch(`${API_BASE_URL}/lots/product/${productId}/last-received`, {
      headers: getHeaders(),
    });
    if (!response.ok) {
      throw new Error('Erreur lors de la récupération du dernier lot reçu');
    }
    return response.json();
  },

  async getProductLots(productId: string) {
    const response = await fetch(`${API_BASE_URL}/lots/product/${productId}`, {
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Erreur lors de la récupération des lots du produit');
    }

    return response.json();
  },

  /**
   * Récupérer le stock total d'un produit (tous lots confondus)
   */
  async getProductStock(productId: string) {
    const response = await fetch(`${API_BASE_URL}/lots/product/${productId}/stock`, {
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Erreur lors de la récupération du stock produit');
    }

    return response.json();
  },

  /**
   * Mettre à jour un lot (DLC, photo étiquette, etc.)
   */
  async updateLot(lotId: string, data: { expiry_date?: string | null; label_photo_url?: string | null }) {
    const response = await fetch(`${API_BASE_URL}/lots/${lotId}`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Erreur lors de la mise à jour du lot');
    }

    return response.json();
  },

  /**
   * Uploader une photo d'étiquette pour un lot
   */
  async uploadLotLabelPhoto(lotId: string, file: File): Promise<{ url: string; label_photo_url: string }> {
    const formData = new FormData();
    formData.append('file', file);

    const headers: HeadersInit = {};
    const token = getAuthToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const tenantId = getTenantId();
    if (tenantId) headers['X-Tenant-ID'] = tenantId;

    const response = await fetch(`${API_BASE_URL}/lots/${lotId}/label-photo`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Erreur lors de l\'upload de la photo');
    }

    return response.json();
  },

  /**
   * Récupérer l'historique des mouvements d'un produit
   */
  async getProductMovements(productId: string) {
    const response = await fetch(`${API_BASE_URL}/lots/product/${productId}/movements`, {
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Erreur lors de la récupération de l\'historique produit');
    }

    return response.json();
  },

  /**
   * Annuler une ligne de « Compter ce produit » (écriture inverse côté serveur).
   */
  async annulerComptage(movementId: string, createdBy?: string) {
    const response = await fetch(`${API_BASE_URL}/lots/movements/${movementId}/annuler-comptage`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ created_by: createdBy }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err?.message || 'Annulation impossible');
    }
    return response.json();
  },

  /**
   * TRAÇABILITÉ AFSCA
   */

  /**
   * Récupérer le document source d'un lot
   */
  async getLotDocument(lotId: string) {
    const response = await fetch(`${API_BASE_URL}/lots/${lotId}/document`, {
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Erreur lors de la récupération du document');
    }

    return response.json();
  },

  /**
   * Récupérer la traçabilité complète d'un produit
   */
  async getProductTraceability(productId: string) {
    const response = await fetch(`${API_BASE_URL}/lots/product/${productId}/traceability`, {
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Erreur lors de la récupération de la traçabilité');
    }

    return response.json();
  },

  /**
   * INVENTAIRE
   */

  /**
   * Récupérer tous les lots pour l'inventaire
   */
  async getInventoryLots() {
    const response = await fetch(`${API_BASE_URL}/lots/inventory`, {
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Erreur lors de la récupération des lots pour inventaire');
    }

    return response.json();
  },

  /**
   * Ajuster le stock d'un lot
   */
  async adjustLotStock(
    lotId: string,
    data: {
      actual_backroom: number;
      actual_display: number;
      reason?: string;
      notes?: string;
      photo_url?: string;
      created_by?: string;
    },
  ) {
    const response = await fetch(`${API_BASE_URL}/lots/${lotId}/adjust`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Erreur lors de l\'ajustement du stock');
    }

    return response.json();
  },

  /**
   * SESSIONS D'INVENTAIRE
   */

  /**
   * Créer une nouvelle session d'inventaire
   */
  async createInventorySession(data: {
    name?: string;
    created_by?: string;
    scope_type?: 'FULL' | 'RAYON';
    scope_category_ids?: string[];
  }) {
    const response = await fetch(`${API_BASE_URL}/inventory/sessions`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Erreur lors de la création de l\'inventaire');
    }

    return response.json();
  },

  /**
   * Lister les sessions d'inventaire
   */
  async listInventorySessions() {
    const response = await fetch(`${API_BASE_URL}/inventory/sessions`, {
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Erreur lors de la récupération des sessions');
    }

    return response.json();
  },

  /**
   * Récupérer une session d'inventaire
   */
  async getInventorySession(sessionId: string) {
    const response = await fetch(`${API_BASE_URL}/inventory/sessions/${sessionId}`, {
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Erreur lors de la récupération de la session');
    }

    return response.json();
  },

  /**
   * Mettre à jour un comptage
   */
  async updateInventoryCount(
    sessionId: string,
    countId: string,
    data: {
      counted_backroom: number;
      counted_display: number;
      notes?: string;
      adjustment_reason?: string;
      counted_by?: string;
    },
  ) {
    const response = await fetch(
      `${API_BASE_URL}/inventory/sessions/${sessionId}/counts/${countId}`,
      {
        method: 'PATCH',
        headers: getHeaders(),
        body: JSON.stringify(data),
      },
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Erreur lors de la mise à jour du comptage');
    }

    return response.json();
  },

  /**
   * Confirmer à 0 toutes les lignes non comptées dont le stock théorique est nul.
   * Les lignes restent dans le registre (inventaire exhaustif), elles sont juste
   * validées d'un geste au lieu d'une par une.
   */
  async countZeroInventoryLines(sessionId: string, data: { counted_by?: string }) {
    const response = await fetch(
      `${API_BASE_URL}/inventory/sessions/${sessionId}/count-zero-lines`,
      {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(data),
      },
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Erreur lors de la confirmation des lignes à 0');
    }

    return response.json() as Promise<{ confirmed: number }>;
  },

  /**
   * Compléter après coup l'explication d'un écart, sur un inventaire DÉJÀ VALIDÉ.
   * L'ajout est daté et signé côté serveur ; la remarque existante est conservée.
   */
  async annotateInventoryCount(
    sessionId: string,
    countId: string,
    data: { note?: string; adjustment_reason?: string },
  ) {
    const response = await fetch(
      `${API_BASE_URL}/inventory/sessions/${sessionId}/counts/${countId}/annotate`,
      { method: 'POST', headers: getHeaders(), body: JSON.stringify(data) },
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Erreur lors de l'ajout de l'explication");
    }
    return response.json();
  },

  /**
   * Rééditer le PDF d'un inventaire validé, explications postérieures incluses.
   * Le document d'origine reste archivé sous son nom.
   */
  async reissueInventoryPdf(sessionId: string) {
    const response = await fetch(
      `${API_BASE_URL}/inventory/sessions/${sessionId}/reissue-pdf`,
      { method: 'POST', headers: getHeaders() },
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Erreur lors de la réédition du PDF');
    }
    return response.json() as Promise<{ pdf_url: string }>;
  },

  /**
   * Corriger le mode de paiement d'une vente. Refuse apres cloture Z, sur un
   * paiement mixte ou sur le porte-monnaie (l'argent a deja bouge).
   */
  async updateSalePaymentMethod(
    saleId: string,
    data: { payment_method: string; reason: string },
  ) {
    const response = await fetch(`${API_BASE_URL}/pos/sales/${saleId}/payment-method`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Erreur lors de la correction du mode de paiement');
    }
    return response.json() as Promise<{ success: boolean; sale_number: string; from: string; to: string }>;
  },

  /**
   * Scinder un lot en deux (colis d'une même livraison portant 2 DLC).
   * Le stock total du produit est inchangé : la quantité indiquée passe sur un
   * lot jumeau, qui hérite de toute la traçabilité de l'original.
   */
  async splitLot(
    lotId: string,
    data: { quantity_backroom?: number; quantity_display?: number; expiry_date: string },
  ) {
    const response = await fetch(`${API_BASE_URL}/lots/${lotId}/split`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Erreur lors de la scission du lot');
    }
    return response.json() as Promise<{ success: boolean; source_lot_number: string; new_lot: any }>;
  },

  /**
   * Valider une session d'inventaire
   */
  async validateInventorySession(sessionId: string, data: { validated_by?: string }) {
    const response = await fetch(
      `${API_BASE_URL}/inventory/sessions/${sessionId}/validate`,
      {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(data),
      },
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Erreur lors de la validation');
    }

    return response.json();
  },

  /**
   * Annuler une session d'inventaire
   */
  async cancelInventorySession(sessionId: string) {
    const response = await fetch(
      `${API_BASE_URL}/inventory/sessions/${sessionId}/cancel`,
      {
        method: 'POST',
        headers: getHeaders(),
      },
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Erreur lors de l\'annulation');
    }

    return response.json();
  },

  /**
   * Couverture d'inventaire par rayon (inventaire tournant)
   */
  async getInventoryCoverage() {
    const response = await fetch(`${API_BASE_URL}/inventory/coverage`, {
      headers: getHeaders(),
    });
    if (!response.ok) {
      throw new Error('Erreur lors de la récupération de la couverture');
    }
    return response.json();
  },

  /**
   * Produits actifs sans catégorie (jamais inventoriés par rayon)
   */
  async getUncategorizedProducts() {
    const response = await fetch(`${API_BASE_URL}/inventory/uncategorized`, {
      headers: getHeaders(),
    });
    if (!response.ok) {
      throw new Error('Erreur lors de la récupération des produits sans catégorie');
    }
    return response.json();
  },

  /**
   * Régler la cadence d'inventaire d'un rayon (jours, null = défaut)
   */
  async updateCategoryCadence(categoryId: string, cadenceDays: number | null) {
    const response = await fetch(
      `${API_BASE_URL}/inventory/categories/${categoryId}/cadence`,
      {
        method: 'PATCH',
        headers: getHeaders(),
        body: JSON.stringify({ cadence_days: cadenceDays }),
      },
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Erreur lors de la mise à jour de la cadence');
    }
    return response.json();
  },

  /**
   * Lister les catégories (pour le sélecteur de rayons)
   */
  async getCategories() {
    const response = await fetch(`${API_BASE_URL}/categories`, {
      headers: getHeaders(),
    });
    if (!response.ok) {
      throw new Error('Erreur lors de la récupération des catégories');
    }
    return response.json();
  },
};
